// The per-day predictability badge over the conditions table's day headings.
//
// The client's reference puts a percentage on each day — "50%", "80%" — with no
// stated derivation. We will not print an opaque number next to a forecast and
// let an operator decide what it means; a percentage on a planning console has
// to be a percentage OF something. So this one is ours, computed from our own
// GEFS ensemble, and the methodology text says exactly how.
//
// The derivation, in one sentence: for each hour of the day we measure the
// ensemble's p95−p05 wind spread, score that hour 1 when the spread is at or
// below the width the briefing already calls "tight" and 0 at or above the
// width it already calls "wide", linearly between, and average the scores over
// the day's hours.
//
// Why those two numbers and not new ones: the window briefing has always said
// "tight — a confident forecast" below 8 kn and "wide — the atmosphere hasn't
// settled" at 15 kn and above. If this badge invented its own thresholds, a day
// could show 85% over a paragraph saying the atmosphere had not settled, and an
// operator who noticed would be right to stop trusting both. They read
// ENSEMBLE_WIDTH_KN from ops/briefing.ts, so there is one pair of numbers.
//
// Why a graded average rather than a count of tight hours: counting puts a
// cliff at the threshold, where a day spread at 7.9 kn scores 100% and one at
// 8.1 kn scores 0%. The average of a graded score moves the way the underlying
// spread moves, which is the only behaviour that survives being watched day
// after day.
//
// What it is NOT: it is not the probability the forecast is correct, and it is
// not a skill score against verification. It is a spread statistic — how much
// the ensemble members disagree with each other — and the help text says so.
// A day with no ensemble coverage returns null and the badge prints nothing;
// there is no number available to guess with.

import { ENSEMBLE_WIDTH_KN } from "../ops/briefing.js";

/** One hour's score from its ensemble width in knots: 1 at or below the tight
 *  threshold, 0 at or above the wide one, linear between. */
export function hourPredictability(widthKn: number): number {
  if (!Number.isFinite(widthKn)) return Number.NaN;
  const { tight, wide } = ENSEMBLE_WIDTH_KN;
  if (widthKn <= tight) return 1;
  if (widthKn >= wide) return 0;
  return (wide - widthKn) / (wide - tight);
}

/**
 * A day's predictability, 0..1, or null when not one of its hours carried an
 * ensemble. Nulls in `widths` are hours with no members and are skipped, not
 * scored as zero — an hour the ensemble never covered is missing data, and
 * scoring it as maximum disagreement would quietly punish the edge of the
 * forecast horizon for a gap in coverage.
 */
export function dayPredictability(widths: readonly (number | null)[]): number | null {
  let sum = 0;
  let n = 0;
  for (const w of widths) {
    if (w === null || !Number.isFinite(w)) continue;
    sum += hourPredictability(w);
    n++;
  }
  return n === 0 ? null : sum / n;
}

/**
 * The badge text, or null for no badge.
 *
 * Rounded to the nearest 5%. The input is a mean of a spread statistic over
 * eight-odd hours; printing it to the unit would claim a precision the
 * measurement does not have, and 5% steps are also what makes a row of badges
 * scannable rather than a row of noise.
 */
export function predictabilityBadge(p: number | null): string | null {
  if (p === null || !Number.isFinite(p)) return null;
  const clamped = p < 0 ? 0 : p > 1 ? 1 : p;
  return `${Math.round((clamped * 100) / 5) * 5}%`;
}

/** The word beside the percentage — the briefing's own three readings of the
 *  same spread, so the badge and the paragraph describe a day alike. */
export function predictabilityWord(p: number | null): string | null {
  if (p === null || !Number.isFinite(p)) return null;
  if (p >= 0.75) return "tight";
  if (p >= 0.35) return "moderate";
  return "wide";
}
